import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { MaisAcoes, type AcaoDoMenu } from '@/componentes/MaisAcoes';
import { RodapeTabela, Tabela, TextoDaCelula } from '@/componentes/Tabela';
import { diaDaOperacao } from '@/lib/dia-da-operacao';
import { formatarDia, formatarInteiro } from '@/lib/formato';
import { EXEMPLO_DO_MOTIVO, faltaNoMotivo } from '@/lib/motivo';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { vigenciaEscrita } from '@/modulos/custos/valor-hora';
import { usePaginacao } from '@/modulos/operacao/paginacao';

import { api, type Funcao, type FuncaoDaPessoa } from './api';
import { faltaNaFuncao, faltaNaFuncaoDaPessoa, funcaoVigente, type ValoresDaFuncaoDaPessoa } from './pessoas';
import { opcaoDaPessoa } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * AS FUNÇÕES E QUEM EXERCE CADA UMA (0095) — a parte "funções" da ferramenta 1
 * do módulo 16 ("relaciona pessoas, funções e equipamentos autorizados"), ao
 * lado das autorizações para operar.
 *
 * A função é a chave do valor-hora de mão de obra, que mora em Custos: aqui não
 * aparece valor nenhum. Trocar a função de alguém muda o custo das horas dele
 * dali em diante — e, com data retroativa, das que já foram vistas —, por isso
 * pede motivo.
 *
 * Sem desenho próprio no manual: segue a seção das autorizações (título,
 * tabela com os cinco estados, ações de linha em "Mais ações") e a janela de até
 * seis campos (p.30).
 */
type QualJanela = 'funcao' | 'pessoa' | 'encerrar' | 'desativar';

const hojeIso = () => diaDaOperacao();

export function FuncoesDasPessoas({
  podeGravar,
  podeLer,
}: {
  readonly podeGravar: boolean;
  /** `documento_de_pessoa.ler`, a licença de `/pessoas/funcoes`: sem ela, a consulta não sai (achado dos robôs, 20/10/2026). */
  readonly podeLer: boolean;
}) {
  const base = useId();
  const cliente = useQueryClient();
  const consulta = useQuery({ queryKey: ['pessoas', 'funcoes'], queryFn: api.funcoes, enabled: podeLer });
  const funcoes = useMemo(() => consulta.data?.funcoes ?? [], [consulta.data]);
  const ativas = useMemo(() => funcoes.filter((f) => f.ativo), [funcoes]);
  const vinculos = useMemo(() => consulta.data?.vinculos ?? [], [consulta.data]);
  const pagina = usePaginacao(vinculos);
  const hoje = hojeIso();

  const [janela, setJanela] = useState<QualJanela | null>(null);
  const [tentou, setTentou] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [vinculo, setVinculo] = useState<ValoresDaFuncaoDaPessoa>({ usuarioId: '', funcaoId: '', desde: hoje, motivo: '' });
  const [alvo, setAlvo] = useState<FuncaoDaPessoa | null>(null);
  const [funcaoAlvo, setFuncaoAlvo] = useState<Funcao | null>(null);
  const [motivo, setMotivo] = useState('');

  const fechar = () => {
    setJanela(null);
    setTentou(false);
  };
  const recarregar = () => cliente.invalidateQueries({ queryKey: ['pessoas'] }).then(() => cliente.invalidateQueries({ queryKey: ['custos'] }));

  const criar = useMutation({
    mutationFn: () => api.criarFuncao({ codigo: codigo.trim(), nome: nome.trim() }),
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Função cadastrada',
        alvo: { codigo: codigo.trim().toUpperCase(), nome: nome.trim() },
        fatos: ['sem pessoas ainda', 'sem valor-hora'],
        efeito: 'Dê o valor-hora em Custos e diga quem a exerce aqui',
      })),
      silenciarFalha: true,
    },
    onSuccess: async () => {
      fechar();
      setCodigo('');
      setNome('');
      await recarregar();
    },
  });

  const definir = useMutation({
    mutationFn: (v: ValoresDaFuncaoDaPessoa) => api.definirFuncaoDaPessoa({ ...v, motivo: v.motivo.trim() }),
    meta: {
      retorno: retornoDe<unknown, ValoresDaFuncaoDaPessoa>((_r, v) => {
        const f = funcoes.find((x) => x.id === v.funcaoId);
        const p = consulta.data?.pessoas.find((x) => x.id === v.usuarioId);
        const antes = vinculos.find((x) => x.usuarioId === v.usuarioId && funcaoVigente(x, v.desde));
        return {
          titulo: 'Função da pessoa definida',
          ...(p ? { alvo: { nome: p.nome } } : {}),
          fatos: [f ? `${f.codigo} · ${f.nome}` : null, `desde ${formatarDia(v.desde)}`, `motivo: ${v.motivo.trim()}`],
          efeito: { rotulo: 'Função', antes: antes ? antes.funcao : 'sem função', depois: f?.nome ?? '' },
        };
      }),
      silenciarFalha: true,
    },
    onSuccess: async () => {
      fechar();
      await recarregar();
    },
  });

  const encerrar = useMutation({
    mutationFn: (v: { readonly id: string; readonly motivo: string }) => api.encerrarFuncaoDaPessoa(v.id, v.motivo),
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Função da pessoa encerrada',
        ...(alvo ? { alvo: { nome: alvo.pessoa } } : {}),
        fatos: [alvo ? `${alvo.funcaoCodigo} · ${alvo.funcao}` : null, `motivo: ${motivo.trim()}`],
        efeito: 'A vigência fechou hoje; as horas de hoje em diante ficam sem função até alguém definir outra',
      })),
      silenciarFalha: true,
    },
    onSuccess: async () => {
      fechar();
      setMotivo('');
      await recarregar();
    },
  });

  const desativar = useMutation({
    mutationFn: (v: { readonly id: string; readonly motivo: string }) => api.desativarFuncao(v.id, v.motivo),
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Função desativada',
        ...(funcaoAlvo ? { alvo: { codigo: funcaoAlvo.codigo, nome: funcaoAlvo.nome } } : {}),
        fatos: [`motivo: ${motivo.trim()}`],
        efeito: 'Sai das escolhas; quem a exerce continua com ela até alguém trocar',
      })),
      silenciarFalha: true,
    },
    onSuccess: async () => {
      fechar();
      setMotivo('');
      await recarregar();
    },
  });

  const faltaFuncao = faltaNaFuncao({ codigo, nome });
  const faltaVinculo = faltaNaFuncaoDaPessoa(vinculo);

  const acoesDaSecao: readonly AcaoDoMenu[] = [
    {
      rotulo: 'Cadastrar função',
      aoEscolher: () => {
        criar.reset();
        setJanela('funcao');
      },
    },
    ...ativas.map((f) => ({
      rotulo: `Desativar ${f.codigo}`,
      aoEscolher: () => {
        desativar.reset();
        setFuncaoAlvo(f);
        setMotivo('');
        setJanela('desativar');
      },
    })),
  ];

  const acoesDaLinha = (v: FuncaoDaPessoa): readonly AcaoDoMenu[] => {
    const indisponivel = !funcaoVigente(v, hoje) ? 'Esta função não vale hoje: não há o que encerrar.' : undefined;
    return [
      {
        rotulo: 'Encerrar função',
        aoEscolher: () => {
          encerrar.reset();
          setAlvo(v);
          setMotivo('');
          setJanela('encerrar');
        },
        ...(indisponivel ? { motivoIndisponivel: indisponivel } : {}),
      },
    ];
  };

  const recusa = (erro: unknown) => (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
      <span className="txt-corpo text-tinta">{explicarFalha(erro).erro}</span>
      <span className="txt-apoio text-tinta-secundaria">{explicarFalha(erro).comoResolver}</span>
    </div>
  );

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 max-w-[80ch] flex-col gap-1">
          <h2 className="txt-secao text-tinta">Funções e quem exerce cada uma</h2>
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            {funcoes.length === 0
              ? 'A função é a chave do valor-hora de mão de obra, que fica em Custos. Nenhum valor aparece aqui.'
              : `${ativas.map((f) => `${f.codigo} · ${f.nome} (${formatarInteiro(f.pessoas)})`).join(' · ')}. O valor-hora de cada uma fica em Custos.`}
          </p>
        </div>
        {/* Sem `aptidao.gravar` a seção é só leitura (prova do marco,
            07/10/2026): antes os itens apareciam desligados, com a licença. */}
        {podeGravar ? (
          <div className="flex items-center gap-2">
            <MaisAcoes acoes={acoesDaSecao} />
            <Botao
              nivel="secundario"
              disabled={ativas.length === 0}
              title={ativas.length === 0 ? 'Cadastre uma função antes, em Mais ações.' : undefined}
              onClick={() => {
                definir.reset();
                setVinculo({ usuarioId: '', funcaoId: '', desde: hoje, motivo: '' });
                setJanela('pessoa');
              }}
            >
              Definir função
            </Botao>
          </div>
        ) : null}
      </div>

      <Tabela
        rotulo="Funções das pessoas"
        colunas={[
          {
            chave: 'pessoa',
            rotulo: 'Pessoa',
            celula: (v) => (
              <span className="flex min-w-0 flex-col">
                <span className="txt-corpo text-tinta">{v.pessoa}</span>
                <TextoDaCelula texto={`${v.funcaoCodigo} · ${v.funcao}`} classe="txt-apoio text-tinta-apagada" linhas={1} />
              </span>
            ),
          },
          {
            chave: 'vigencia',
            rotulo: 'Vigência',
            celula: (v) => <span className="numero whitespace-nowrap text-tinta">{vigenciaEscrita(v)}</span>,
          },
          {
            chave: 'situacao',
            rotulo: 'Situação',
            celula: (v) =>
              funcaoVigente(v, hoje) ? (
                <span className="txt-corpo text-tinta">Vale hoje</span>
              ) : (
                <span className="txt-apoio text-tinta-secundaria">{v.desde > hoje ? 'Começa depois' : 'Encerrada'}</span>
              ),
          },
          {
            chave: 'quem',
            rotulo: 'Registrado por',
            prioridade: 'secundaria',
            celula: (v) => <span className="txt-apoio text-tinta-secundaria">{v.registradoPor ?? '—'}</span>,
          },
        ]}
        linhas={pagina.visiveis}
        rodape={<RodapeTabela {...pagina.rodape} />}
        chaveDaLinha={(v) => v.id}
        carregando={consulta.isPending}
        alturaDupla
        {...(podeGravar ? { acoes: (v: FuncaoDaPessoa) => <MaisAcoes acoes={acoesDaLinha(v)} /> } : {})}
        erro={consulta.isError ? <ErroDaLista oQue="As funções das pessoas" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
        vazio={
          <EstadoVazio
            titulo={funcoes.length === 0 ? 'Nenhuma função cadastrada' : 'Ninguém com função definida'}
            descricao={
              funcoes.length === 0
                ? 'Cadastre as funções da operação — operador de escavadeira, mecânico, ajudante — em Mais ações. Sem função, a hora da pessoa fica fora do custo.'
                : 'Diga quem exerce cada função e desde quando. Sem função, a hora da pessoa fica fora do custo de mão de obra.'
            }
          />
        }
      />

      <Janela
        titulo="Cadastrar função"
        aberta={janela === 'funcao' && podeGravar}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              nivel="primario"
              disabled={criar.isPending}
              onClick={() => {
                setTentou(true);
                if (Object.keys(faltaFuncao).length === 0) criar.mutate();
              }}
            >
              {criar.isPending ? 'Gravando…' : 'Cadastrar função'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {criar.isError ? recusa(criar.error) : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <CampoTexto rotulo="Código" valor={codigo} aoMudar={setCodigo} placeholder="OPESC" validar={() => faltaFuncao.codigo ?? null} />
            <div className="sm:col-span-2">
              <CampoTexto rotulo="Nome" valor={nome} aoMudar={setNome} placeholder="Operador de escavadeira" validar={() => faltaFuncao.nome ?? null} />
            </div>
          </div>
          {tentou && (faltaFuncao.codigo || faltaFuncao.nome) ? (
            <p className="txt-ajuda text-erro">{faltaFuncao.codigo ?? faltaFuncao.nome}</p>
          ) : null}
          <p className="txt-apoio text-pretty text-tinta-secundaria">A função é o que dá preço à hora: o valor-hora dela se define em Custos.</p>
        </div>
      </Janela>

      <Janela
        titulo="Definir função"
        descricao="A função anterior da pessoa fecha na data escolhida. Na mesma data de início, corrige."
        aberta={janela === 'pessoa' && podeGravar}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              nivel="primario"
              disabled={definir.isPending}
              onClick={() => {
                setTentou(true);
                if (Object.keys(faltaVinculo).length === 0) definir.mutate(vinculo);
              }}
            >
              {definir.isPending ? 'Gravando…' : 'Definir função'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {definir.isError ? recusa(definir.error) : null}
          <CampoSelecao
            rotulo="Pessoa"
            valor={vinculo.usuarioId}
            aoMudar={(usuarioId) => {
              setVinculo({ ...vinculo, usuarioId });
            }}
            opcoes={(consulta.data?.pessoas ?? []).map((p) => opcaoDaPessoa(p))}
            rotuloVazio="Escolha a pessoa"
          />
          {tentou && faltaVinculo.usuarioId ? <p className="txt-ajuda text-erro">{faltaVinculo.usuarioId}</p> : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CampoSelecao
              rotulo="Função"
              valor={vinculo.funcaoId}
              aoMudar={(funcaoId) => {
                setVinculo({ ...vinculo, funcaoId });
              }}
              opcoes={ativas.map((f) => ({ valor: f.id, rotulo: `${f.codigo} · ${f.nome}` }))}
              rotuloVazio="Escolha a função"
            />
            <Campo id={`${base}-desde`} rotulo="Desde" erro={tentou ? (faltaVinculo.desde ?? null) : null} ajuda="Pode ser uma data passada.">
              <input
                id={`${base}-desde`}
                type="date"
                className="controle"
                value={vinculo.desde}
                onChange={(e) => {
                  setVinculo({ ...vinculo, desde: e.target.value });
                }}
              />
            </Campo>
          </div>
          {tentou && faltaVinculo.funcaoId ? <p className="txt-ajuda text-erro">{faltaVinculo.funcaoId}</p> : null}
          <CampoDoMotivo
            valor={vinculo.motivo}
            aoMudar={(m) => {
              setVinculo({ ...vinculo, motivo: m });
            }}
            exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['funcao.trocar']}`}
          />
        </div>
      </Janela>

      <Janela
        titulo={alvo ? `Encerrar a função de ${alvo.pessoa}?` : 'Encerrar função'}
        descricao="A vigência fecha hoje e a linha fica no histórico. As horas de hoje em diante ficam sem função, e fora do custo, até alguém definir outra."
        aberta={janela === 'encerrar' && podeGravar}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fechar}>
              Manter função
            </Botao>
            <Botao
              nivel="destrutivo"
              disabled={encerrar.isPending || faltaNoMotivo(motivo) !== null}
              onClick={() => {
                if (alvo) encerrar.mutate({ id: alvo.id, motivo: motivo.trim() });
              }}
            >
              {encerrar.isPending ? 'Encerrando…' : 'Encerrar função'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {encerrar.isError ? recusa(encerrar.error) : null}
          <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['funcao.trocar']}`} />
        </div>
      </Janela>

      <Janela
        titulo={funcaoAlvo ? `Desativar a função ${funcaoAlvo.codigo}?` : 'Desativar função'}
        descricao="A função sai das escolhas. Quem a exerce continua com ela, e o valor-hora continua valendo, até alguém trocar."
        aberta={janela === 'desativar' && podeGravar}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fechar}>
              Manter função
            </Botao>
            <Botao
              nivel="destrutivo"
              disabled={desativar.isPending || faltaNoMotivo(motivo) !== null}
              onClick={() => {
                if (funcaoAlvo) desativar.mutate({ id: funcaoAlvo.id, motivo: motivo.trim() });
              }}
            >
              {desativar.isPending ? 'Desativando…' : 'Desativar função'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {desativar.isError ? recusa(desativar.error) : null}
          <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['cadastro.desativar']}`} />
        </div>
      </Janela>
    </>
  );
}
